/**
 * Sign your declarations: the last step before an onboarding case is submitted (owner, 2026-10-24).
 *
 * Two statements, each printed by the server from the client's own answers and shown read-only:
 * the tax residence self-certification (every country of tax residence with its tax number, or the
 * reason there is none) and the declarations (own account, public positions, source of the money).
 * The client adds only what the profile cannot hold, types their legal name, and signs once. The
 * words signed are the server's, in the language of the screen, sealed with a version: what is shown
 * is what is signed. The submit of the case is refused until both are current, and a change in the
 * answers makes them stale, so this panel asks again.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import CountryPicker from '../CountryPicker';
import kycStatementsService, {
  type ExtraInput,
  type ReasonInput,
  type StatementsPreview,
  type StatementView,
} from '../../services/kycStatements.service';
import { getApiErrorMessage } from '../../lib/apiError';
import {
  EMPTY_EXTRA,
  NEEDS_EXPLANATION,
  allSigned,
  canSign,
  missingThings,
  typedInput,
} from '../../lib/kycStatements';
import { activeLanguage } from '../../i18n/language';
import { colors } from '../../styles/theme';

interface Props {
  /** The onboarding case being filled in. */
  readonly caseId: string;
  /** Called after both statements were signed, so the page can show the submit as ready. */
  readonly onSigned?: () => void;
}

const FIELD = {
  background: colors.canvas,
  border: `1.5px solid ${colors.border}`,
  color: colors.ink,
} as const;

const RELOAD_AFTER_MS = 450;

function Box({
  children,
  tone = 'info',
}: Readonly<{ children: React.ReactNode; tone?: 'info' | 'warn' | 'ok' }>): JSX.Element {
  const palette = {
    info: { bg: colors.infoBg, border: colors.infoBorder },
    warn: { bg: colors.warningBg, border: '#FCD34D' },
    ok: { bg: colors.successBg, border: colors.successBorder },
  }[tone];
  return (
    <div
      className="rounded-lg p-3 mb-3 text-sm"
      role={tone === 'warn' ? 'alert' : undefined}
      style={{ background: palette.bg, border: `1px solid ${palette.border}`, color: colors.ink }}
    >
      {children}
    </div>
  );
}

export function StatementBlock({ statement }: Readonly<{ statement: StatementView }>): JSX.Element {
  return (
    <section
      className="rounded-lg p-4 mb-4"
      style={{ background: colors.canvas, border: `1px solid ${colors.border}` }}
      translate="no"
    >
      <h4 className="text-sm font-semibold mb-2" style={{ color: colors.ink }}>
        {statement.title}
      </h4>
      <dl className="grid sm:grid-cols-3 gap-y-2 gap-x-6 mb-3">
        {statement.lines.map((line) => (
          <div key={`${line.label}-${line.value}`} className="sm:col-span-3 grid sm:grid-cols-3 gap-x-6">
            <dt className="text-xs" style={{ color: colors.muted }}>{line.label}</dt>
            <dd className="sm:col-span-2 text-sm" style={{ color: colors.ink }}>{line.value || '—'}</dd>
          </div>
        ))}
      </dl>
      {statement.body.map((paragraph) => (
        <p key={paragraph} className="text-xs mb-1" style={{ color: colors.inkSoft, lineHeight: 1.55 }}>
          {paragraph}
        </p>
      ))}
    </section>
  );
}

function ReasonPicker({
  id,
  reasons,
  value,
  onChange,
}: Readonly<{
  id: string;
  reasons: StatementsPreview['no_tin_reasons'];
  value: ReasonInput;
  onChange: (next: ReasonInput) => void;
}>): JSX.Element {
  return (
    <div className="grid gap-2" translate="no">
      <select
        id={id}
        value={value.reason}
        onChange={(e) => onChange({ ...value, reason: e.target.value })}
        className="rounded-lg px-3 py-2 text-sm"
        style={FIELD}
      >
        <option value="" />
        {reasons.map((r) => (
          <option key={r.code} value={r.code}>{r.label}</option>
        ))}
      </select>
      {value.reason === NEEDS_EXPLANATION && (
        <input
          aria-label="Explanation"
          maxLength={200}
          value={value.explanation}
          onChange={(e) => onChange({ ...value, explanation: e.target.value })}
          className="rounded-lg px-3 py-2 text-sm"
          style={FIELD}
        />
      )}
    </div>
  );
}

export default function KycStatementsPanel({ caseId, onSigned }: Readonly<Props>): JSX.Element | null {
  const [preview, setPreview] = useState<StatementsPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [signError, setSignError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reasons, setReasons] = useState<Record<string, ReasonInput>>({});
  const [extras, setExtras] = useState<ExtraInput[]>([]);
  // One stable id per extra row, parallel to `extras`, so a removal never hands a row another row's DOM state.
  const [extraKeys, setExtraKeys] = useState<number[]>([]);
  const nextExtraKey = useRef(0);

  const patchExtra = (index: number, patch: Partial<ExtraInput>, clearTin = false): void => {
    setExtras((prev) => prev.map((x, i) => (i === index ? { ...x, ...patch, tin: clearTin ? '' : (patch.tin ?? x.tin) } : x)));
  };
  const removeExtra = (index: number): void => {
    setExtras((prev) => prev.filter((_, i) => i !== index));
    setExtraKeys((prev) => prev.filter((_, i) => i !== index));
  };
  const addExtra = (): void => {
    setExtras((prev) => [...prev, { ...EMPTY_EXTRA }]);
    setExtraKeys((prev) => [...prev, nextExtraKey.current++]);
  };
  const [signerName, setSignerName] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const language = activeLanguage();
  const latest = useRef(0);

  const load = useCallback(async () => {
    const mine = ++latest.current;
    try {
      const loaded = await kycStatementsService.preview(
        caseId,
        typedInput(language, { reasons, extras, signerName, confirmed, mfaCode }),
      );
      if (mine === latest.current) {
        setPreview(loaded);
        setLoadError(null);
      }
    } catch (e: unknown) {
      if (mine === latest.current) {
        setLoadError(getApiErrorMessage(e, "We couldn't prepare your declarations. Please try again shortly."));
      }
    }
    // Only what the server prints depends on these; the name and the code do not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, language, reasons, extras]);

  useEffect(() => {
    const timer = setTimeout(() => void load(), RELOAD_AFTER_MS);
    return () => clearTimeout(timer);
  }, [load]);

  async function sign(): Promise<void> {
    if (!preview || busy) return;
    setBusy(true);
    setSignError(null);
    try {
      await kycStatementsService.sign(caseId, {
        ...typedInput(language, { reasons, extras, signerName, confirmed, mfaCode }),
        signerName: signerName.trim(),
        mfaCode: preview.step_up_required ? mfaCode : null,
        shownSha256: preview.shown_sha256,
      });
      setConfirmed(false);
      setMfaCode('');
      await load();
      onSigned?.();
    } catch (e: unknown) {
      setSignError(getApiErrorMessage(e, "We couldn't sign your declarations. Please try again."));
      await load();
    } finally {
      setBusy(false);
    }
  }

  if (loadError) return <Box tone="warn">{loadError}</Box>;
  if (!preview) return <p className="text-sm" style={{ color: colors.muted }}>Preparing your declarations…</p>;
  if (!preview.applies || !preview.editable) return null;

  const entries = { reasons, extras, signerName, confirmed, mfaCode };
  const ready = canSign(preview, entries);
  const missing = missingThings(preview.missing);
  const signed = allSigned(preview);
  const canAdd = extras.length < preview.max_extra_jurisdictions;
  const signedLabel = signed ? 'Sign again' : 'Sign electronically';
  const signLabel = busy ? 'Signing…' : signedLabel;

  return (
    <section
      className="rounded-xl p-4 mb-6"
      style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      aria-labelledby="kyc-statements-title"
    >
      <h3 id="kyc-statements-title" className="text-base font-semibold mb-1" style={{ color: colors.ink }}>
        Sign your declarations
      </h3>
      <p className="text-sm mb-3" style={{ color: colors.inkSoft }}>
        Before you submit, read and sign these two statements. Everything in them comes from your
        answers, so you do not type it again. If something is wrong, correct your answers first.
      </p>

      {preview.acts_for_another && (
        <Box tone="warn">
          We can only open an account for the person who will contribute. If you are helping someone
          else, ask them to open their own account.
        </Box>
      )}

      {missing.length > 0 && (
        <Box tone="warn">
          <p>Your answers are missing what the statements need. Add it first, then come back.</p>
          <ul className="list-disc pl-5 mt-1">
            {missing.map((thing) => (
              <li key={thing}>{thing}</li>
            ))}
          </ul>
        </Box>
      )}

      {signed && <Box tone="ok">Your declarations are signed. You can submit.</Box>}

      {preview.statements.map((statement) => (
        <StatementBlock key={statement.kind} statement={statement} />
      ))}

      {preview.countries_needing_a_reason.length > 0 && (
        <div className="mb-4">
          <p className="text-sm font-semibold mb-1" style={{ color: colors.ink }}>
            We have no tax number for these countries
          </p>
          <p className="text-xs mb-2" style={{ color: colors.muted }}>
            Say why you have none. A tax number you do have belongs in your profile.
          </p>
          {preview.countries_needing_a_reason.map((country) => (
            <div key={country} className="mb-2 max-w-md">
              <label htmlFor={`reason-${country}`} className="block text-xs mb-1" style={{ color: colors.muted }}>
                <span translate="no">{country}</span>
              </label>
              <ReasonPicker
                id={`reason-${country}`}
                reasons={preview.no_tin_reasons}
                value={reasons[country] ?? { reason: '', explanation: '' }}
                onChange={(next) => setReasons((prev) => ({ ...prev, [country]: next }))}
              />
            </div>
          ))}
        </div>
      )}

      <div className="mb-4">
        <p className="text-sm font-semibold mb-1" style={{ color: colors.ink }}>
          Are you a tax resident of another country?
        </p>
        {extras.map((extra, index) => (
          <div key={extraKeys[index]} className="grid gap-2 mb-3 max-w-md p-3 rounded-lg" style={{ border: `1px solid ${colors.border}` }}>
            <CountryPicker
              id={`extra-country-${index}`}
              value={extra.country || null}
              onChange={(code) => patchExtra(index, { country: code ?? '' })}
            />
            <input
              aria-label="Tax identification number"
              autoComplete="off"
              maxLength={40}
              value={extra.tin}
              disabled={extra.reason !== ''}
              onChange={(e) => patchExtra(index, { tin: e.target.value })}
              className="rounded-lg px-3 py-2 text-sm font-mono"
              style={FIELD}
            />
            <ReasonPicker
              id={`extra-reason-${index}`}
              reasons={preview.no_tin_reasons}
              value={{ reason: extra.reason, explanation: extra.explanation }}
              onChange={(next) =>
                patchExtra(index, { reason: next.reason, explanation: next.explanation }, next.reason !== '')
              }
            />
            <button
              type="button"
              className="text-xs text-left"
              style={{ color: colors.muted }}
              onClick={() => removeExtra(index)}
            >
              Remove this country
            </button>
          </div>
        ))}
        {canAdd && (
          <button
            type="button"
            className="text-sm font-semibold"
            style={{ color: colors.ink }}
            onClick={addExtra}
          >
            Add a country of tax residence
          </button>
        )}
      </div>

      {preview.error && !signed && missing.length === 0 && <Box tone="warn">{preview.error}</Box>}

      {/* The signature is the last entry: nothing below it is read or asked. */}
      <div className="grid gap-3 max-w-md mb-3">
        <label className="flex items-start gap-2 text-sm" style={{ color: colors.ink }}>
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            style={{ marginTop: 3 }}
          />
          <span>I have read both statements above and make them under oath to tell the truth.</span>
        </label>
        {preview.step_up_required && (
          <div>
            <label htmlFor="kyc-statements-code" className="block text-xs mb-1" style={{ color: colors.muted }}>
              Code from your authenticator app
            </label>
            <input
              id="kyc-statements-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
              className="rounded-lg px-3 py-2 text-sm font-mono w-40"
              style={FIELD}
            />
          </div>
        )}
        <div>
          <label htmlFor="kyc-statements-signer" className="block text-xs mb-1" style={{ color: colors.muted }}>
            To sign, type your full legal name
          </label>
          <input
            id="kyc-statements-signer"
            autoComplete="off"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            className="rounded-lg px-3 py-2 text-sm w-full"
            style={FIELD}
          />
          <p className="text-xs mt-1" style={{ color: colors.muted }}>
            Your name, the date and time, and a statement that you signed electronically are
            recorded with each statement.
          </p>
        </div>
      </div>

      {signError && <Box tone="warn">{signError}</Box>}

      <button
        type="button"
        onClick={() => void sign()}
        disabled={!ready || busy}
        className="px-4 py-2 rounded-lg text-sm font-semibold"
        style={{
          background: ready && !busy ? colors.gold : colors.disabledBg,
          color: colors.navy,
          cursor: ready && !busy ? 'pointer' : 'not-allowed',
        }}
      >
        {signLabel}
      </button>
      <p className="sr-only" aria-live="polite">{signError ?? ''}</p>
    </section>
  );
}
